/* eslint-disable react/prop-types */
// Disables react/prop-types ESLint rule, since TypeScript already handles prop validation.
import { useState, useEffect, useMemo } from 'react';
import { useField } from 'formik';
import { FAIcon, InputValidations, Theme, useTranslation } from '@nextgen-web-framework/all';
import { debounce } from 'lodash';
import usePasswordValidation from '../../../../utils/customHooks/usePasswordValidation';
import { PasswordInputFieldProps, PasswordInputFieldStateType } from '../../types/propTypes';
import AmwayPasswordField from '../../amway-password-field/AmwayPasswordField';
import styles from '../password-without-confirm/PasswordWithoutConfirm.module.scss';
import PasswordStrengthMeter from '../../../password-strength/PasswordStrengthMeter';

interface PasswordWithoutConfirmProps extends PasswordInputFieldProps {
  onPasswordChange?: (password: string) => void;
  onPasswordMeetsMinimumChange?: (meetsMinimum: boolean) => void;
}

const PasswordWithoutConfirm: React.FC<PasswordWithoutConfirmProps> = ({
  onPasswordChange,
  onPasswordMeetsMinimumChange,
  ...props
}) => {
  const t = useTranslation();
  const { forceError, error, onInputChangeCallback, required = true } = props;
  const [field] = useField(props.name);
  const [errors, validatePassword] = usePasswordValidation();
  const [isFocused, setIsFocused] = useState(false);
  const [showError, setShowError] = useState(false);
  const [password, setPassword] = useState<string>('');
  const [debouncedPassword, setDebouncedPassword] = useState<string>('');
  const [passwordMeetsMinimum, setPasswordMeetsMinimum] = useState<boolean>(false);
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const defaultPlaceholder = t('identity.CreatePasswordForm.createPasswordPlaceholderLabel');

  // Detect first interaction with the password field
  useEffect(() => {
    if (isFocused && !userHasInteracted) {
      setUserHasInteracted(true);
    }
  }, [isFocused]);

  const handlePasswordMeetsMinimumChange = (meetsMinimum: boolean) => {
    setPasswordMeetsMinimum(meetsMinimum);
    if (onPasswordMeetsMinimumChange) {
      onPasswordMeetsMinimumChange(meetsMinimum);
    }
  };

  // Memoize the debounced function so that it is created only once (or only when onPasswordChange changes)
  const debouncedUpdate = useMemo(
    () =>
      debounce((newPassword: string) => {
        setDebouncedPassword(newPassword);
        if (onPasswordChange) {
          onPasswordChange(newPassword);
        }
      }, 200),
    [onPasswordChange],
  );

  const [state, setState] = useState<PasswordInputFieldStateType>({
    type: 'password',
    iconName: 'eye-slash',
  });

  const { type, iconName } = state;
  const { NEXT_PUBLIC_IDENTITY_ALLOW_PASSWORD_STRENGTH_METER: allowPasswordStrengthMeter } = ConfigService.getConfigs();

  useEffect(() => {
    debouncedUpdate(password);
    return () => {
      debouncedUpdate.cancel();
    };
  }, [password]);

  useEffect(() => {
    if (debouncedPassword) {
      validatePassword(debouncedPassword);
    }
  }, [debouncedPassword]);

  const passwordErrorMessage = (
    <>
      {!allowPasswordStrengthMeter && (
        <>
          {t('identity.PasswordWithoutConfirm.heading')}
          <br />
          <ul className={styles['password-rules']}>
            <div className={styles['bullet-point-rules']}>
              <li>{t('identity.PasswordWithoutConfirm.infoText2')}</li>
              <li>{t('identity.PasswordWithoutConfirm.infoText3')}</li>
              <li>{t('identity.PasswordWithoutConfirm.infoText4')}</li>
              <li>{t('identity.PasswordWithoutConfirm.infoText5')}</li>
            </div>
          </ul>
        </>
      )}
    </>
  );

  let status: InputValidations;
  // Keep validation active once the user has interacted
  if (
    !forceError &&
    (!userHasInteracted || (allowPasswordStrengthMeter && (passwordMeetsMinimum || password.length === 0)))
  ) {
    status = InputValidations.DEFAULT;
  } else if (allowPasswordStrengthMeter || showError || forceError) {
    status = InputValidations.INVALID;
  } else {
    status = InputValidations.DEFAULT;
  }
  const message = status === InputValidations.INVALID && forceError ? error : '';
  return (
    <>
      <AmwayPasswordField
        values={password}
        type={type}
        inputIcon={{ iconName }}
        inputIconOnClick={() => {
          setState((prevState) => ({
            type: prevState.type === 'password' ? 'text' : 'password',
            iconName: prevState.type === 'password' ? 'eye' : 'eye-slash',
          }));
        }}
        {...field}
        {...props}
        required={required}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          field.onChange(e);
          setPassword(e.target.value);
          onInputChangeCallback?.(e);
        }}
        status={status}
        onBlur={() => {
          errors[0] ? setShowError(true) : setShowError(false);
          setIsFocused(false);
        }}
        onFocus={() => setIsFocused(true)}
        placeholder={props.label || defaultPlaceholder}
        message={message}
      />
      {allowPasswordStrengthMeter === true && !forceError && (
        <PasswordStrengthMeter
          password={debouncedPassword}
          isFocused={isFocused}
          onPasswordMeetsMinimumChange={handlePasswordMeetsMinimumChange}
          isPasswordHistoryError={props?.error}
        />
      )}
      <div className={styles['sections']}>
        {allowPasswordStrengthMeter !== true && showError ? (
          <div className={styles['left-section-red']}>
            <div className="icon-section">
              <FAIcon theme={Theme.ERROR} iconName="square-exclamation" />
            </div>
            <div data-testid="error-msg-test-id" className={styles['error-section']}>
              {passwordErrorMessage}
            </div>
          </div>
        ) : isFocused ? (
          <div className={styles['left-section-default']}>
            <div data-testid="error-msg-test-id" className={styles['error-section']}>
              {passwordErrorMessage}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default PasswordWithoutConfirm;
